import React, { FC, useEffect, useMemo } from 'react';

import 'react-toastify/dist/ReactToastify.css';
import dayjs from 'dayjs';
import en from 'dayjs/locale/en';
import
{
	alephToast,
	NotificationToast,
	NotificationType,
	PasswordResetPage,
	RequestPasswordResetPage,
	LoginBlock,
	AdBlockModal,
} from '@aleph/shared-components';
import RedditResponse from 'wb-reddit/app/routes/superGrid/tool/advertiserOAuth/redditResponse';
import AppRoutes from 'wb-reddit/app/routes/index';
import * as Sentry from '@sentry/react';
import AuthProvider from 'auth/lib/provider';
import useAccessTokenStore from 'auth/lib/useAccessTokenStore';
import logoImg from 'wb-reddit/assets/logo-wiseblue.svg';
import RedditSideMenu from 'wb-reddit/app/common/components/sideMenu';
import { getPublicClient, REFRESH_TOKEN_URI } from 'wb-reddit/app/common/utils';
import { RequestResetPasswordDocument, ResetPasswordDocument } from 'wb-reddit/graphql/generated';
import { useMatch, useNavigate, useSearchParams } from 'react-router-dom';

function getSAMLEmailDomain() {
	const final = ['wise.blue'];

	if (import.meta.env.VITE_DEVELOPMENT === 'true') {
		// final.push('httpool.com');
		final.push('alephholding.com');
	}

	return final;
}

function buildSAMLRedirectPath() {
	return [
		import.meta.env.VITE_API_PATH,
		'/auth/saml/login/pn_re/user?redirect=',
		window.location.origin,
		'?samlrefresh=true',
	].join('');
}

const IGNORED_ERROR_MESSAGES = [
	'ResizeObserver loop limit exceeded',
	'ResizeObserver loop completed with undelivered notifications.',
];

export const onSessionExpired = () => {
	alephToast(
		({ closeToast }) => (
			<NotificationToast
				closeToast={closeToast}
				type={NotificationType.Warning}
				title={<div>Session Expired!</div>}
				description={<div>You have been logged out due to inactivity!</div>}
			/>
		),
		{ position: 'top-center' },
	);
};

const App: FC = () => {
	const publicClient = useMemo(() => getPublicClient({
		uri: `${import.meta.env.VITE_API_PATH}/graphql/public/pn_re/`,
	}), []);

	const showRequestPasswordReset = useMatch('/requestPasswordReset');
	const showResetPassword = useMatch('/resetPassword');
	const navigate = useNavigate();
	const [urlParams] = useSearchParams();

	useEffect(() => {
		const ignoreErrors = (e) => {
			if (IGNORED_ERROR_MESSAGES.includes(e.message)) {
				const resizeObserverErrDiv = document.getElementById(
					'webpack-dev-server-client-overlay-div',
				);
				const resizeObserverErr = document.getElementById(
					'webpack-dev-server-client-overlay',
				);

				if (resizeObserverErr) {
					resizeObserverErr.setAttribute('style', 'display: none');
				}

				if (resizeObserverErrDiv) {
					resizeObserverErrDiv.setAttribute('style', 'display: none');
				}
			}
		};
		window.addEventListener('error', ignoreErrors);

		return () => {
			window.removeEventListener('error', ignoreErrors);
		};
	}, []);

	useEffect(() => {
		if (import.meta.env.VITE_PRODUCTION === 'true') {
			// @ts-ignore it does exist - defined in index.html
			window?.heap?.load?.('847156942');
		}

		dayjs.locale({
			...en,
			weekStart: 1,
		});

		Sentry.setUser({ token: useAccessTokenStore.getState().accessToken ?? 'Unknown' });
	}, []);

	const logo = (
		<img
			alt="logo"
			className="w-[174px] h-[80px]"
			src={logoImg}
		/>
	);

	const params = new URLSearchParams(window.location.search);

	// we need to memoize it to avoid re-rendering the whole app every time navigate() is called
	const AppComponent = useMemo(() => (
		<AuthProvider
			loginUri={`${import.meta.env.VITE_API_PATH}/auth/token/pn_re/user/`}
			logoutUri={`${import.meta.env.VITE_API_PATH}/auth/logout/pn_re/user/`}
			graphQLUri={`${import.meta.env.VITE_API_PATH}/graphql/pn_re/`}
			refreshTokenUri={REFRESH_TOKEN_URI}
			onSessionExpired={onSessionExpired}
		>
			{
				({
					isLoggedIn, login, logout, refreshToken,
				}) => (
					isLoggedIn ? <>
						<RedditResponse />
						<RedditSideMenu logout={logout} />
						<AppRoutes />
						<AdBlockModal knowledgeBaseUrl="https://knowledge.wise.blue/wise-blue-for-reddit" />
					</> : <LoginBlock
						logo={logo}
						refreshToken={refreshToken}
						login={login}
						certPath={`${import.meta.env.VITE_API_PATH}/cert-email/`}
						SAML={{
							emailDomains: getSAMLEmailDomain(),
							loginPath:    buildSAMLRedirectPath(),
						}}
						navigate={navigate}
						urlParams={urlParams}
					/>
				)
			}
		</AuthProvider>
	), []);

	if (showRequestPasswordReset) {
		return (
			<RequestPasswordResetPage
				logo={logo}
				navigate={navigate}
				onRequest={async (userEmail) => {
					await publicClient?.mutate({
						mutation:  RequestResetPasswordDocument,
						variables: { userEmail },
					});
				}}
			/>
		);
	}

	if (showResetPassword) {
		return (
			<PasswordResetPage
				logo={logo}
				navigate={navigate}
				type={params.get('type')}
				onReset={async (userPassword) => {
					await publicClient?.mutate({
						mutation:  ResetPasswordDocument,
						variables: { userPassword, token: params?.get('token') ?? '' },
					});
				}}
			/>
		);
	}

	return AppComponent;
};

export default App;
